import {
  BuildingOffice2Icon,
  EnvelopeIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline'
import { useState } from 'react'
import { useNotification } from '../../context/NotificationContext'

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function Contact() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    message: '',
  })
  const [formErrors, setFormErrors] = useState({})
  const [isFormValid, setIsFormValid] = useState(false)
  const { addNotification } = useNotification()

  const handleChange = (event) => {
    setFormData({ ...formData, [event.target.name]: event.target.value })
    setIsFormValid(
      formData.email.trim() !== '' &&
        formData.message.trim() !== '' &&
        emailRegex.test(formData.email)
    )
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!validateForm()) return

    const apiUrl = process.env.NEXT_PUBLIC_CONTACT_EMAIL_SENDER

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, emailTo: 'contact@openspeech.io' }),
      })
      if (!response.ok) {
        addNotification({
          id: Date.now(),
          message: 'Message failed to send',
          type: 'error',
        })
      }
      // Do something with the response, if needed

      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        message: '',
      })

      addNotification({
        id: Date.now(),
        message: 'Message sent successfully',
        type: 'success',
      })
    } catch (error) {
      addNotification({
        id: Date.now(),
        message: 'Message failed to send',
        type: 'error',
      })
    }
  }

  const validateForm = () => {
    let errors = {}

    if (!formData.email.trim()) {
      errors.email = 'Email is required.'
    } else if (!emailRegex.test(formData.email)) {
      errors.email = 'Invalid email format.'
    }

    if (!formData.message.trim()) {
      errors.message = 'Message is required.'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  return (
    <div className="relative isolate bg-white">
      {/* Header section */}
      <div className="px-6 pt-16 lg:px-8">
        <h1 className="mx-auto max-w-2xl text-center text-5xl font-bold tracking-tight">
          {'Feel Free to Connect with Us Anytime.'}
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-center leading-8 text-gray-600">
          {
            "We're all ears here at OpenSpeech! If you've got any questions, ideas, or simply want to chat, don't hesitate to get in touch. We're always ready to hear your thoughts and look forward to connecting with you."
          }{' '}
        </p>
      </div>
      <div className="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-2">
        <div className="relative px-6 sm:pt-12 lg:static lg:px-8">
          <div className="mx-auto max-w-xl lg:mx-0 lg:max-w-lg">
            <dl className="mt-10 space-y-4 text-base leading-7 text-gray-600">
              <div className="flex gap-x-4">
                <dt className="flex-none">
                  <span className="sr-only">Telephone</span>
                  <BuildingOffice2Icon
                    className="h-7 w-6 text-gray-400"
                    aria-hidden="true"
                  />
                </dt>
                <dd>
                  167-169 GREAT PORTLAND STREET
                  <br />
                  LONDON, ENGLAND
                  <br />
                  W1W 5PF
                </dd>
              </div>
              <div className="flex gap-x-4">
                <dt className="flex-none">
                  <span className="sr-only">Telephone</span>
                  <EnvelopeIcon
                    className="h-7 w-6 text-gray-400"
                    aria-hidden="true"
                  />
                </dt>
                <dd>
                  <a
                    className="hover:text-gray-900"
                    href="mailto:contact@openspeech.io"
                  >
                    contact@openspeech.io
                  </a>
                </dd>
              </div>
            </dl>
          </div>
        </div>
        <form
          onSubmit={handleSubmit}
          className="px-6 pb-24 pt-20 sm:pb-32 lg:px-8"
        >
          <div className="mx-auto max-w-xl lg:mr-0 lg:max-w-lg">
            <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold leading-6 text-gray-900"
                >
                  Email
                </label>
                <div className="mt-2.5">
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 ${
                      formErrors.email ? 'border-2 border-red-600' : ''
                    }`}
                  />
                  {formErrors.email && (
                    <p className="mt-2 text-sm text-red-600">
                      {formErrors.email}
                    </p>
                  )}
                  {formErrors.message && (
                    <p className="mt-2 text-sm text-red-600">
                      {formErrors.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="sm:col-span-2">
                <label
                  htmlFor="message"
                  className="block text-sm font-semibold leading-6 text-gray-900"
                >
                  Message
                </label>
                <div className="mt-2.5">
                  <textarea
                    name="message"
                    id="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </div>
            <div className="mt-8 flex justify-end">
              <button
                type="submit"
                disabled={!isFormValid}
                className={`rounded-md bg-indigo-600 px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 ${
                  !isFormValid ? 'cursor-not-allowed opacity-50' : ''
                }`}
              >
                Send message
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
